import { useEffect, useState, useMemo, useContext } from 'react';
import { Outlet } from 'react-router-dom';
import Header from './Header';
import {
  Box,
  CssBaseline,
  Backdrop,
  CircularProgress,
} from '@mui/material';
import CustomDialog from '../ui/feedback/CustomDialog';
import { useLocation } from 'react-router-dom';
import { WebSocketProvider } from '../../context/WebSocketContext';
import useSessionStorage from '../../hooks/useSessionStorage';
import { AssessmentProvider } from '../../context/AssessmentContext';
import { KioskContext } from '../../context/KioskProviderContext';
import { useTranslation } from 'react-i18next';
import SecureWrapper from '../secure-wrapper/SecureWrapper';

const Layout = ({ disableMenu, login, landing, practice, preview }) => {
  const { isLoading, setIsLoading } = useContext(KioskContext);
  const [errorDialogProps, setErrorDialogProps] = useState();
  const [errorWithSetup, setErrorWithSetup] = useState(false);
  const { hasSessionStorage, getStorageItem, clearStorage } =
    useSessionStorage();
  const { state } = useLocation();
  const authToken = getStorageItem('authToken');
  const { t } = useTranslation();

  const accessCode = state?.accessCode;

  const accessErrorActions = useMemo(() => {
    return [
      {
        label: t('common.login'),
        handle: () => {
          setErrorWithSetup(false);
          clearStorage(); //this is to handle cases where the user refreshes, the auth token might already be cleared but not all storage.
          window.location.href = '/login';
        },
      },
    ];
  }, [clearStorage, t]);

  useEffect(() => {    
    if (!hasSessionStorage) {
      setErrorWithSetup(true);
      setErrorDialogProps({
        title: t('components.common.errorWithApplicationSetup'),
        message: t('components.common.sessionStorageUnsupported'),
      });
    }
    if (!login && !landing && !practice && !preview && !authToken) {
      setErrorWithSetup(true);
      setErrorDialogProps({
        title: t('components.common.accessDenied'),
        message: t('components.common.pleaseLoginForService'),
        actions: accessErrorActions,
      });
    }
    if(login || landing){
      clearStorage();
    }
  }, [
    hasSessionStorage,
    login,
    landing,
    accessErrorActions,
    authToken,
    practice,
    preview,
    t,
    clearStorage
  ]);

  //Clear storage is back or forward button is pressed
  window.onpopstate = (event) => {
    if (hasSessionStorage) {
      clearStorage();
    }
  };

  //The landing route used to build its own markup inline, it now goes through
  //getLayout as well so SecureWrapper is mounted on every route - the secure
  //wrapper can request an exit before the tester ever reaches a test. login is
  //no longer passed in, the prop is read straight off the component props.
  const getLayout = () => {
    if (landing) {
      return (
        <>
          <SecureWrapper />
          <Box display="flex">
            <Header />
            <Box style={{ width: '100vw', height: `calc(100vh - 100px)` }}>
              <Outlet />
            </Box>
          </Box>
        </>
      );
    }
    return (
      <>
        <SecureWrapper />
        <Box
          sx={{
            height: '100%',
            width: '100%',
          }}
        >
          <CssBaseline />
          <Header disableMenu={disableMenu} />
          <Box
            component="main"
            sx={{
              display: 'flex',
              flexGrow: 1,
              overflow: 'hidden',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              ...(login
                ? { height: `calc(100vh - 67px)` }
                : { height: '100%' }),
              marginTop: '64px',
              zIndex: (theme) => theme.zIndex.drawer,
            }}
          >
            <Outlet context={[setIsLoading, errorWithSetup]} />
          </Box>
        </Box>
      </>
    );
  };

  return (
    <>
      <CssBaseline />
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <CustomDialog open={errorWithSetup} dialogProps={errorDialogProps} />
      {(landing || login) && getLayout()}
      {(preview || practice) && (
        <AssessmentProvider>{getLayout()}</AssessmentProvider>
      )}
      {!login && !landing && authToken && (
        <AssessmentProvider
          {...(state ? { loginData: { accessCode: accessCode } } : {})}
        >
          <WebSocketProvider
            wssURL={`${state.wssURL}`}
            authToken={state.authToken}
          >
            {getLayout()}
          </WebSocketProvider>
        </AssessmentProvider>
      )}
    </>
  );
};

export default Layout;
